import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { VerticalCardLoader } from 'syf-component-library/ui/atoms';
import { ModalBody, ModalWrapper, STATUS_CODE } from '@account-services-mfe/core';
import { PaymentSecurityScreens } from '../../../typings';
import Footer from './footer/Footer';
import displayModalScreen from '../../../helpers/display-modal-screen';
import { usePaymentSecurityContext } from '../../../context';
import { useDebtCancellationGet, useDebtCancellationPut, useUtilityJWETokenGet } from '../../../helpers/syf-api-queries';
import triggerPaymentSecurityAnalytics from '../../../helpers/trigger-payment-security-analytics';
import { PAGE_NAME } from '../../../const/analytics';
import queryClient from '../../../const/query-client';
import { debtCancellationCacheKey } from '../../../services/get/debt-cancellation-get-cache-key';
import submitJWEToAON from '../../../helpers/submit-jwe-to-aon';
const PaymentSecurityModalContent = () => {
    const { handleClose, selectedAccountId, currentPaymentSecurityScreen, setCurrentPaymentSecurityScreen, tokenDetails, setTokenDetails, hidePopup, authorizeAONUrl } = usePaymentSecurityContext();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { data: getCancellationData, isFetching: isGetCancellationFetching, isError: isGetCancellationError, error: getCancellationError } = useDebtCancellationGet(selectedAccountId);
    const { mutateAsync, isLoading: isPutCancellationLoading } = useDebtCancellationPut(selectedAccountId);
    const { data: getTokenData, isFetching: isGetTokenFetching, error: getTokenError, isError: isGetTokenError } = useUtilityJWETokenGet(selectedAccountId, tokenDetails.shouldFetchToken);
    const handleCloseModal = () => {
        triggerPaymentSecurityAnalytics({
            pageFunction: 'account services',
            pageKind: 'account services'
        });
        handleClose();
    };
    const handleResponseError = useCallback((status) => {
        if (status === STATUS_CODE.UNPROCESSABLE_ENTITY) {
            triggerPaymentSecurityAnalytics({
                errorMessage: 'You’re unable to purchase Payment Security at this time',
                pageName: PAGE_NAME.DENIED_ENROLLMENT
            });
            setCurrentPaymentSecurityScreen(PaymentSecurityScreens.DENIED_ENROLLMENT);
        }
        else {
            triggerPaymentSecurityAnalytics({
                errorMessage: 'We’re having trouble retrieving your information',
                pageName: PAGE_NAME.API_ERROR
            });
            setCurrentPaymentSecurityScreen(PaymentSecurityScreens.GENERIC_ERROR);
        }
    }, [setCurrentPaymentSecurityScreen]);
    const handleEnrollSubmit = async () => {
        setCurrentPaymentSecurityScreen(PaymentSecurityScreens.DEFAULT);
        setIsSubmitting(true);
        try {
            await mutateAsync({
                'cipher.accountId': selectedAccountId,
                accountIdType: 'guid',
                enrolled: true
            });
            setCurrentPaymentSecurityScreen(PaymentSecurityScreens.ENROLLMENT_SUCCESS);
            // Invalidate GET to refresh latest data
            await queryClient.invalidateQueries(debtCancellationCacheKey({
                accountId: selectedAccountId,
                accountIdType: 'guid'
            }));
        }
        catch (error) {
            handleResponseError(error.response?.status ??
                STATUS_CODE.INTERNAL_SERVER_ERROR);
        }
    };
    // Handle Enrollment Status Screen
    useEffect(() => {
        if (!isGetCancellationFetching && !isSubmitting && getCancellationData) {
            const enrollmentStatus = getCancellationData?.enrollmentStatus.toLowerCase();
            setCurrentPaymentSecurityScreen(enrollmentStatus);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getCancellationData, isGetCancellationFetching, isSubmitting]);
    useEffect(() => {
        if (!isSubmitting &&
            !isGetCancellationFetching &&
            isGetCancellationError &&
            getCancellationError) {
            handleResponseError(getCancellationError.response?.status ??
                STATUS_CODE.INTERNAL_SERVER_ERROR);
        }
    }, [
        getCancellationError,
        handleResponseError,
        isGetCancellationError,
        isGetCancellationFetching,
        isSubmitting
    ]);
    // Handle JWE Token Fetching
    useEffect(() => {
        if (isGetTokenFetching) {
            setTokenDetails({ ...tokenDetails, isFetchingToken: true });
        }
    }, [isGetTokenFetching, setTokenDetails, tokenDetails]);
    // Handle JWE Token Error
    useEffect(() => {
        if (isGetTokenError && getTokenError) {
            triggerPaymentSecurityAnalytics({
                errorMessage: 'We’re having trouble retrieving your JWE token',
                pageName: PAGE_NAME.API_ERROR
            });
            setCurrentPaymentSecurityScreen(PaymentSecurityScreens.GENERIC_ERROR);
            hidePopup();
        }
    }, [
        isGetTokenError,
        getTokenError,
        hidePopup,
        setCurrentPaymentSecurityScreen
    ]);
    // Handles the JWE Token
    useEffect(() => {
        if (!isGetTokenFetching &&
            tokenDetails.shouldFetchToken &&
            getTokenData &&
            authorizeAONUrl) {
            submitJWEToAON(getTokenData.jweToken, authorizeAONUrl);
            setTokenDetails({
                ...tokenDetails,
                shouldFetchToken: false,
                isFetchingToken: false
            });
            hidePopup();
        }
    }, [
        isGetTokenFetching,
        getTokenData,
        hidePopup,
        setTokenDetails,
        tokenDetails,
        authorizeAONUrl
    ]);
    if (isGetCancellationFetching || isPutCancellationLoading) {
        return (_jsx("div", { "data-testid": "ps-skeleton-loader", children: _jsx(VerticalCardLoader, {}) }));
    }
    return (_jsxs(ModalWrapper, { children: [_jsx(ModalBody, { children: displayModalScreen(currentPaymentSecurityScreen) }), _jsx(Footer, { currentScreen: currentPaymentSecurityScreen, handleClose: handleCloseModal, handleEnrollSubmit: handleEnrollSubmit })] }));
};
export default PaymentSecurityModalContent;
